/**
 * ToolbarMenuGroup — collapses a group of chart-toolbar controls (Indicators,
 * Candle types, Strategies, Option-action buttons, Symbol filters, Timeframes)
 * behind a single trigger, with a FAVOURITES system:
 *   • items the user pins (★) render inline on the toolbar, always one click away;
 *   • every item (pinned or not) is available in a vertical popover that opens from
 *     the trigger, where each row can be toggled and pinned/unpinned;
 *   • the trigger shows how many items in the group are currently active, with a dot
 *     when an active item is hidden in the menu (so nothing "on" is invisible).
 *
 * Favourites + (where relevant) the controls' own state persist via usePersistedState,
 * so the layout survives reloads. Pure presentational shell — each item supplies its
 * own active flag + onClick, so it works for toggles (indicators), exclusive choices
 * (candle type / strategy / filters) and momentary actions (option builds) alike.
 *
 * STYLING (2026-09-02 redesign)
 * Every group used to carry its own pastel background tint — 13 groups, 13 hues,
 * no hierarchy: the toolbar told you which GROUP a control belonged to, which the
 * label already said, and never told you which controls were ON. Containers are
 * now uniformly neutral (chGroupSx) and colour is reserved for state (chChipSx):
 * filled accent = active. The `accent` prop is kept for call-site compatibility
 * but no longer paints anything — see styles/appUi.ts.
 */
import React, { useState, useMemo } from 'react';
import { Box, Chip, Popover, Typography, IconButton, Tooltip, Badge, Divider } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { usePersistedState } from '../../hooks/usePersistedState';
import { useAppTheme } from '../../hooks/useAppTheme';
import { CH_TYPE, chGroupSx, chGroupLabelSx, chChipSx, type ChTone } from '../../styles/appUi';

export type ChipColorName = 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

export interface ToolbarItem {
  key: string;
  label: string;
  tooltip?: string;
  active?: boolean;      // highlighted (filled) when true
  onClick: () => void;
  /** @deprecated Superseded by `tone`. Retained so existing call sites compile. */
  color?: ChipColorName;
  /** Semantic tone when active. Defaults to 'accent' ("switched on"). Use bull /
   *  bear only where the control really is directional. */
  tone?: ChTone;
}

interface Props {
  groupId: string;       // persistence key suffix
  label: string;         // group name on the trigger
  items: ToolbarItem[];
  /** @deprecated Inert since the 2026-09-02 redesign — group containers are
   *  uniformly neutral and colour marks state, not category. Kept so the ~13
   *  call sites need not all change at once. */
  accent?: { dark: string; light: string };
  isDark: boolean;
  defaultFavourites?: string[];
  /** Hide the trigger's active count (e.g. for momentary-action groups). */
  hideActiveCount?: boolean;
  /** Tighter chips + padding, for the narrow side panel. */
  dense?: boolean;
  /** Optional row pinned under the list inside the popover. The timeframe group
   *  uses it for "Manage custom intervals"; every other group leaves it unset. */
  footer?: React.ReactNode;
}


const ToolbarMenuGroup: React.FC<Props> = ({ groupId, label, items, isDark, defaultFavourites = [], hideActiveCount, dense, footer }) => {
  const { C } = useAppTheme();
  const chipSx = chChipSx(C, isDark, { dense });
  const [fav, setFav] = usePersistedState<string[]>(`chart.toolbarFav.${groupId}.v1`, defaultFavourites);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const favSet = useMemo(() => new Set(fav), [fav]);

  const favItems = items.filter(i => favSet.has(i.key));
  const activeCount = items.filter(i => i.active).length;
  const hiddenActive = items.some(i => i.active && !favSet.has(i.key));

  const toggleFav = (key: string) => setFav(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);


  const renderChip = (it: ToolbarItem) => (
    <Tooltip key={it.key} title={it.tooltip || ''} arrow disableHoverListener={!it.tooltip}>
      <Chip label={it.label} size="small" variant="outlined"
        onClick={it.onClick}
        sx={chChipSx(C, isDark, { active: it.active, tone: it.tone, dense })}
      />
    </Tooltip>
  );

  return (
    <Box sx={chGroupSx(isDark, dense)}>
      <Typography sx={chGroupLabelSx}>{label}</Typography>
      {favItems.map(renderChip)}
      {/* Trigger → full list popover */}
      <Tooltip arrow title={`All ${label.toLowerCase()} — toggle & pin ★ favourites`}>
        <Badge color="warning" variant="dot" overlap="circular" invisible={!hiddenActive}>
          <Chip
            size="small"
            onClick={(e) => setAnchor(e.currentTarget)}
            icon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            label={hideActiveCount ? 'More' : (activeCount > 0 ? `${activeCount}` : 'All')}
            variant="outlined"
            sx={{ ...chipSx, fontWeight: 800, '& .MuiChip-icon': { ml: 0.2, mr: -0.4 } }}
          />
        </Badge>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1, maxHeight: 460, width: 248 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: CH_TYPE.body, fontWeight: 800 }}>{label}</Typography>
          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', ml: 'auto' }}>★ pin to the bar</Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
          {items.map(it => {
            const pinned = favSet.has(it.key);
            return (
              <Box key={it.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.3, py: 0.15, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}>
                <Tooltip arrow title={it.tooltip || ''} disableHoverListener={!it.tooltip} placement="left">
                  <Chip label={it.label} size="small" variant="outlined"
                    onClick={it.onClick}
                    sx={{ ...chChipSx(C, isDark, { active: it.active, tone: it.tone }),
                          flex: 1, justifyContent: 'flex-start',
                          '& .MuiChip-label': { px: 0.6, width: '100%' } }}
                  />
                </Tooltip>
                <Tooltip arrow title={pinned ? 'Unpin from bar' : 'Pin to bar'}>
                  <IconButton size="small" onClick={() => toggleFav(it.key)} sx={{ p: 0.3, color: pinned ? 'warning.main' : 'text.disabled' }}>
                    {pinned ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
        {footer && (
          <>
            <Divider sx={{ mt: 0.75, mb: 0.5 }} />
            {footer}
          </>
        )}
      </Popover>
    </Box>
  );
};

export default ToolbarMenuGroup;
